/**
 * This component uses its own query to get the time series data, so it's kinda secondary.
 */

import { gql } from '@apollo/client';
import { Box, MenuItem, Skeleton, Typography } from '@mui/material';
import { Tooltip as CommonTooltip } from 'components/common/Tooltip';
import { CreatorAvatar } from 'features/creator';
import { processGraphData } from 'features/socialMediaListening/hooks/useGraphDataMakerNew';
import {
  ChartDataPoint,
  CompetitorShareOfVoiceFiltersInput,
  CreatorFragmentCreatorAvatarFragmentDoc,
  useGetCompetitorShareOfVoiceTimeSeriesDataForBrandCompetitorsShareOfVoiceLineChartQuery,
} from 'graphql/generated';
import moment from 'moment';
import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';
import {
  AggregationType,
  AggregationTypeLabelMap,
} from './BrandCompetitorsShareOfVoiceView';

// eslint-disable-next-line
gql`
  query GetCompetitorShareOfVoiceTimeSeriesDataForBrandCompetitorsShareOfVoiceLineChart(
    $filters: CompetitorShareOfVoiceFiltersInput!
    $skip: Int
    $take: Int
  ) {
    competitorShareOfVoiceTimeSeriesData(
      filters: $filters
      skip: $skip
      take: $take
    ) {
      data {
        id
        base {
          id
          brand {
            id
            name
            creators {
              id
              ...CreatorFragmentCreatorAvatar
            }
          }
          competitor {
            id
            name
          }
        }
        dataPoints {
          date
          data {
            shareOfVoice
            mentionCount
            viewCount
            totalEngagement
            engagementRate
          }
        }
      }
      pageInfo {
        hasNextPage
      }
    }
  }
  ${CreatorFragmentCreatorAvatarFragmentDoc}
`;

export type BrandCompetitorsShareOfVoiceLineChartProps = {
  filters: Pick<
    CompetitorShareOfVoiceFiltersInput,
    'platforms' | 'dateRange' | 'brandId'
  >;
  aggregationType: AggregationType;
  selectedItemIds: string[];
  itemIdToColorMap: Record<string, string>;
  activeItemId?: string;
  onCompetitorClick?: (
    competitorTrackingBrandId: string,
    dateRange?: {
      startDate: Date;
      endDate: Date;
    },
  ) => void;
};

export const BrandCompetitorsShareOfVoiceLineChart = (
  props: BrandCompetitorsShareOfVoiceLineChartProps,
) => {
  const {
    filters,
    aggregationType,
    selectedItemIds,
    itemIdToColorMap,
    activeItemId,
    onCompetitorClick,
  } = props;

  const { data: timeSeriesData, loading } =
    useGetCompetitorShareOfVoiceTimeSeriesDataForBrandCompetitorsShareOfVoiceLineChartQuery(
      {
        variables: {
          filters,
          take: 99,
        },
        fetchPolicy: 'cache-and-network',
      },
    );
  const allItems = useMemo(
    () => timeSeriesData?.competitorShareOfVoiceTimeSeriesData.data ?? [],
    [timeSeriesData],
  );
  const displayedItems = allItems.filter((item) =>
    selectedItemIds.includes(item.id),
  );

  // For each item to display, we need to process the data points
  // and construct the final array used for the line chart.
  const data = useMemo(() => {
    const finalDataByKey: Record<string, Record<string, any>> = {};
    const dailyChartSumDataByDate: Record<string, number> = {};

    // If aggregation type is share of voice, we need to build a map of the sum of all selected items for each date
    // We want the sum of SELECTED items to always amount to 100%, so this map + processGraphData will allow us
    // to scale up the value of each data point.
    if (aggregationType === AggregationType.ShareOfVoice) {
      displayedItems.forEach((item) => {
        item.dataPoints.forEach((dataPoint) => {
          dailyChartSumDataByDate[dataPoint.date] =
            (dailyChartSumDataByDate[dataPoint.date] || 0) +
            dataPoint.data.shareOfVoice;
        });
      });
    }

    // Next, loop through each item to display, and prepare the chart data points.
    displayedItems.forEach((item) => {
      // Prepare the raw chart data points before processing
      const rawChartDataPoints: ChartDataPoint[] = item.dataPoints.map(
        (dataPoint) => {
          let yValue = 0;

          switch (aggregationType) {
            case AggregationType.ShareOfVoice:
              yValue = dataPoint.data.shareOfVoice * 100;
              break;
            case AggregationType.MentionCount:
              yValue = dataPoint.data.mentionCount;
              break;
            case AggregationType.ViewCount:
              yValue = dataPoint.data.viewCount;
              break;
            case AggregationType.TotalEngagement:
              yValue = dataPoint.data.totalEngagement;
              break;
            case AggregationType.EngagementRate:
              yValue = dataPoint.data.engagementRate * 100;
              break;
            default:
              break;
          }

          return {
            X: dataPoint.date,
            Y: yValue,
          };
        },
      );

      // Use processGraphData to get the processed chart data
      // which contains built-in date-range grouping
      const processedChartData = processGraphData({
        dailyChartData: rawChartDataPoints,
        dateRange: [filters.dateRange.startDate, filters.dateRange.endDate],
        groupValueAggregationType:
          aggregationType === AggregationType.ShareOfVoice ||
          aggregationType === AggregationType.EngagementRate
            ? 'avg'
            : 'sum',
        dailyChartDataForAvg:
          aggregationType === AggregationType.ShareOfVoice
            ? Object.entries(dailyChartSumDataByDate).map(([date, sum]) => ({
                X: date,
                Y: sum,
              }))
            : undefined,
      });

      // Next, append the processed chart data to the final data array.
      // Grouped by X, and then by item id.
      processedChartData.forEach((dataPoint) => {
        finalDataByKey[dataPoint.X] = {
          ...finalDataByKey[dataPoint.X],
          date: dataPoint.X,
          dateRange: dataPoint.dateRange,
          [item.id]: dataPoint.Y,
        };
      });
    });

    return Object.values(finalDataByKey);
  }, [JSON.stringify({ displayedItems, aggregationType, filters })]); // eslint-disable-line

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        height={300}
        sx={{
          borderRadius: 4,
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 20,
        }}
      >
        <Typography
          variant="headline-sm"
          color={theme.colors?.utility[700]}
          sx={{
            textAlign: 'center',
            writingMode: 'sideways-lr',
            height: 300,
          }}
        >
          {AggregationTypeLabelMap[aggregationType]}
        </Typography>
      </Box>
      <ResponsiveContainer
        width="100%"
        height={300}
        style={{
          flex: 1,
        }}
      >
        <LineChart data={data}>
          <CartesianGrid
            vertical={false}
            stroke={theme.colors?.utility[400]}
            strokeDasharray="4"
          />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            style={{
              ...theme.typography['headline-sm'],
              color: theme.colors?.utility[700],
            }}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            style={{
              ...theme.typography['headline-sm'],
              color: theme.colors?.utility[700],
            }}
            tickFormatter={(value) => {
              switch (aggregationType) {
                case AggregationType.ShareOfVoice:
                case AggregationType.EngagementRate:
                  return `${value.toFixed(0)}%`;
                default:
                  return formatBigNumber(Number(value));
              }
            }}
            max={
              aggregationType === AggregationType.ShareOfVoice ? 100 : undefined
            }
          />
          {displayedItems?.map((item) => (
            <Line
              type="monotone"
              key={item.id}
              dataKey={item.id}
              stroke={itemIdToColorMap[item.id]}
              strokeWidth={2}
              dot={false}
              strokeOpacity={
                activeItemId ? (activeItemId === item.id ? 1 : 0.5) : 1
              }
              activeDot={{
                r: 5,
                stroke: itemIdToColorMap[item.id],
                strokeWidth: 2,
              }}
            />
          ))}
          <Tooltip
            wrapperStyle={{
              pointerEvents: 'auto',
              zIndex: 1,
            }}
            cursor={{ stroke: theme.colors?.utility[300], strokeWidth: 1 }}
            // eslint-disable-next-line
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const originalPayload = payload[0].payload;

                const dateRange = originalPayload.dateRange;
                const isWithinTheSameDay = moment(dateRange[0]).isSame(
                  dateRange[1],
                  'day',
                );

                const keys = Object.keys(originalPayload);
                const competitorIds = keys.filter(
                  (key) => key !== 'date' && key !== 'dateRange',
                );
                const competitors = allItems.filter((item) =>
                  competitorIds.includes(item.id),
                );

                return (
                  <Box
                    onMouseMove={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    sx={{
                      borderRadius: 2.5,
                      border: `1px solid ${theme.colors?.utility[100]}`,
                      boxShadow: '0px 2.56px 12px 0px #00000014',
                      padding: 8,
                      bgcolor: theme.colors?.primary.white,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="subhead-xl"
                      color={theme.colors?.utility[600]}
                    >
                      {isWithinTheSameDay
                        ? moment(dateRange[0]).format('MMM D, YYYY')
                        : `${moment(dateRange[0]).format(
                            'MMM D, YYYY',
                          )} - ${moment(dateRange[1]).format('MMM D, YYYY')}`}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      {competitors.map((competitor) => {
                        let value = originalPayload[competitor.id];

                        switch (aggregationType) {
                          case AggregationType.ShareOfVoice:
                          case AggregationType.EngagementRate:
                            value = `${value.toFixed(2)}%`;
                            break;
                          default:
                            value = formatBigNumber(Number(value));
                        }

                        const creatorWithProfilePicture =
                          competitor.base.brand.creators.find(
                            (creator) => creator.profilePictureUrl,
                          );

                        return (
                          <CommonTooltip title="View posts" key={competitor.id}>
                            <MenuItem
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                borderRadius: 2,
                                minWidth: 300,
                                mx: -4,
                              }}
                              onClick={() => {
                                onCompetitorClick?.(competitor.base.brand.id, {
                                  startDate: dateRange[0],
                                  endDate: dateRange[1],
                                });
                              }}
                            >
                              <Typography
                                variant="headline-xl"
                                sx={{
                                  fontSize: 16,
                                  color: itemIdToColorMap[competitor.id],
                                  minWidth: '30%',
                                  flexShrink: 0,
                                }}
                              >
                                {value}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                }}
                              >
                                {creatorWithProfilePicture && (
                                  <CreatorAvatar
                                    creator={creatorWithProfilePicture}
                                    size={20}
                                    hidePlatformIcon
                                  />
                                )}
                                <Typography
                                  variant="subhead-xl"
                                  sx={{
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {competitor.base.brand.name}
                                </Typography>
                              </Box>
                            </MenuItem>
                          </CommonTooltip>
                        );
                      })}
                    </Box>
                  </Box>
                );
              }

              return null;
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};
